// userWheelFrame.js — build a PER-USER Wheel-Portrait-Frame ring from that user's
// OWN primary/generated wheel segments. Gordon law `all-avatars-wheel-frames`
// (refined 2026-08-12): each avatar frame renders THAT USER'S own wheel, unique per
// person, IDENTICAL on every surface (chat/gallery/notifications/header) — the frame
// is tied to the PERSON, not the place. The default on-brand SVG
// (assets/images/backgroundWheelProfile.svg) is the fallback ONLY when a user has
// no wheel.
//
// Reuses the canonical brand-wheel geometry (BrandWheel/brandWheelInner — G-19) so
// there is NO hand-rolled donut: we feed it the user's real segment colours + spans
// (normalised value -> degrees) instead of the brand demo palette. A white backing
// circle makes the parallel-edged gaps read as white hairlines on any surface
// (white-segment-hairline law).
import { brandWheelInner, BRAND_WHEEL_PALETTE } from "../BrandWheel/brandWheel.js";

/**
 * @param {Array<{color?:string, value?:number}>} segments  the user's wheel_segments
 * @returns {string|null} an <svg> string for the user's ring, or null when the user
 *   has no usable wheel (caller falls back to the default frame asset).
 */
export function userWheelFrameSVG(segments) {
  if (!Array.isArray(segments) || segments.length < 1) return null;

  // Normalise each segment's `value` to a degree span summing to 360 so the ring is
  // a full circle regardless of the raw value scale. Zero/absent values fall back to
  // equal spans so a wheel with unset values still renders a complete ring.
  const vals = segments.map((s) => {
    const v = Number(s && s.value);
    return Number.isFinite(v) && v > 0 ? v : 0;
  });
  const total = vals.reduce((a, b) => a + b, 0);
  const spans =
    total > 0
      ? vals.map((v) => (v > 0 ? (v / total) * 360 : 0))
      : segments.map(() => 360 / segments.length);
  // guard: if normalisation produced any zero spans (mixed set/unset values), even them out
  const spansSafe = spans.some((s) => s <= 0.01)
    ? segments.map(() => 360 / segments.length)
    : spans;

  const colors = segments.map(
    (s, i) =>
      (s && typeof s.color === "string" && /^#([0-9a-f]{3,8})$/i.test(s.color)
        ? s.color
        : BRAND_WHEEL_PALETTE[i % BRAND_WHEEL_PALETTE.length])
  );

  const inner = brandWheelInner({
    spans: spansSafe,
    colors,
    gap: 4,
    ratio: 0.75, // brand Portrait ratio — do not change
    disc: true,
  });

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" ` +
    `viewBox="0 0 120 120" fill="none" aria-hidden="true" style="display:block">` +
    `<circle cx="60" cy="60" r="57" fill="#ffffff"/>${inner}</svg>`
  );
}

/** data-URI form for use as a CSS background-image, mirroring the default asset URL. */
export function userWheelFrameDataUri(segments) {
  const svg = userWheelFrameSVG(segments);
  return svg ? "data:image/svg+xml;utf8," + encodeURIComponent(svg) : null;
}
